import { useEffect, useState } from 'react';
import api from '../../api/axios';
import MasterEditModal from '../../components/MasterEditModal';
import { storageUrl } from '../../utils/urls';

const MAX_AUCTIONS = 5;

export default function Branches() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', address: '', image: null });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', address: '', image: null, is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const reachedAuctionLimit = rows.length >= MAX_AUCTIONS;

  const load = () => api.get('/auctions').then((r) => setRows(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const add = async () => {
    if (reachedAuctionLimit) {
      setError(`لا يمكن إنشاء أكثر من ${MAX_AUCTIONS} مزادات.`);
      return;
    }
    if (!form.name.trim()) return;
    setError('');
    try {
      const payload = new FormData();
      payload.append('name', form.name.trim());
      payload.append('phone', form.phone || '');
      payload.append('address', form.address || '');
      if (form.image) payload.append('image', form.image);

      await api.post('/auctions', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ name: '', phone: '', address: '', image: null });
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر إضافة المزاد.');
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      name: row.name || '',
      phone: row.phone || '',
      address: row.address || '',
      image: null,
      is_active: row.is_active,
    });
  };

  const update = async () => {
    if (!editRow || !editForm.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = new FormData();
      payload.append('name', editForm.name.trim());
      payload.append('phone', editForm.phone || '');
      payload.append('address', editForm.address || '');
      payload.append('is_active', editForm.is_active ? '1' : '0');
      if (editForm.image) payload.append('image', editForm.image);

      await api.put(`/auctions/${editRow.id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditRow(null);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر حفظ المزاد.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm('تأكيد حذف المزاد؟')) return;
    setError('');
    try {
      await api.delete(`/auctions/${row.id}`);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || 'تعذر حذف المزاد.');
    }
  };

  return (
    <div>
      <div className="grid gap-2 md:grid-cols-5 mb-3">
        <input
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          className="rounded-lg border p-2"
          placeholder="اسم المزاد"
        />
        <input
          value={form.phone}
          onChange={(e) => setField('phone', e.target.value)}
          className="rounded-lg border p-2"
          placeholder="رقم هاتف المزاد"
        />
        <input
          value={form.address}
          onChange={(e) => setField('address', e.target.value)}
          className="rounded-lg border p-2"
          placeholder="عنوان المزاد"
        />
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setField('image', e.target.files?.[0] || null)}
          className="rounded-lg border p-2 text-sm file:ml-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5"
        />
        <button
          onClick={add}
          disabled={reachedAuctionLimit}
          className={`rounded-lg px-3 py-2 text-white ${reachedAuctionLimit ? 'cursor-not-allowed bg-slate-400' : 'bg-primary'}`}
        >
          إضافة مزاد
        </button>
      </div>

      {reachedAuctionLimit && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          لا يمكن إنشاء أكثر من {MAX_AUCTIONS} مزادات.
        </div>
      )}

      {error && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr>
            <th className="p-2 text-right">الصورة</th>
            <th className="p-2 text-right">المزاد</th>
            <th className="p-2 text-right">الهاتف</th>
            <th className="p-2 text-right">العنوان</th>
            <th className="p-2 text-right">الحالة</th>
            <th className="p-2">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="p-2">
                {row.image_path ? (
                  <img src={storageUrl(row.image_path)} alt={row.name} className="h-10 w-10 rounded-lg border bg-white object-cover" />
                ) : (
                  <span className="inline-grid h-10 w-10 place-items-center rounded-lg border bg-slate-50 text-xs text-slate-400">بدون</span>
                )}
              </td>
              <td className="p-2">{row.name}{row.is_main ? ' - الرئيسي' : ''}</td>
              <td className="p-2">{row.phone || '-'}</td>
              <td className="p-2">{row.address || '-'}</td>
              <td className="p-2">{row.is_active ? 'نشط' : 'موقوف'}</td>
              <td className="p-2 flex gap-2 justify-center">
                <button onClick={() => openEdit(row)} className="px-2 py-1 border rounded">تعديل</button>
                {!row.is_main && <button onClick={() => remove(row)} className="px-2 py-1 border rounded text-red-600">حذف</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <MasterEditModal
        open={Boolean(editRow)}
        title="تعديل المزاد"
        subtitle="عدّل بيانات المزاد ويمكنك تحديث صورته من هنا."
        fields={[
          { name: 'name', label: 'اسم المزاد', required: true },
          { name: 'phone', label: 'رقم الهاتف' },
          { name: 'address', label: 'العنوان' },
          { name: 'image', label: 'صورة المزاد', type: 'file', accept: 'image/*' },
          { name: 'is_active', label: 'نشط', type: 'checkbox' },
        ]}
        values={editForm}
        onChange={(key, value) => setEditForm((current) => ({ ...current, [key]: value }))}
        onClose={() => setEditRow(null)}
        onSubmit={update}
        saving={saving}
      />
    </div>
  );
}
